import React, { useState } from "react";
import { Button, Popover, Spin } from "antd";
import {
  CarryOutOutlined, FieldTimeOutlined, LoadingOutlined, MoreOutlined,
} from "@ant-design/icons";
import { isActionRead } from "./esvActionDisplay";
import { canEditEsvReadStatus, canEditEsvStatus, normalizeEsvClearance } from "./EsvDataOpsCell";
import ConfirmationModal from "../../Common/ConfirmationModal";

const confirmationBody = (label, selectedCount) => {
  const countLabel = selectedCount === 1
    ? "this selected ESV action"
    : `these ${selectedCount} selected ESV actions`;
  if (label === "Mark as Read") {
    return `Are you sure you want to mark ${countLabel} as read?`;
  }
  if (label === "Mark as Unread") {
    return `Are you sure you want to mark ${countLabel} as unread?`;
  }
  if (label === "Mark as Pending") {
    return `Are you sure you want to set the Data Ops status to Pending for ${countLabel}?`;
  }
  if (label === "Mark as Cleared") {
    return `Are you sure you want to set the Data Ops status to Cleared for ${countLabel}?`;
  }
  return `Are you sure you want to apply ${label} to ${countLabel}?`;
};

const ActionOption = ({ option, onSelect }) => (
  <Button
    className="field-button"
    type="text"
    icon={option.icon}
    disabled={option.disabled}
    onClick={() => {
      if (option.disabled) return;
      onSelect(option);
    }}
  >
    {option.label}
  </Button>
);

const actionSpin = (
  <Spin className="EsvRowActions__Spin" indicator={<LoadingOutlined spin />} size="small" />
);

const ActionMenu = ({ groups, onSelect }) => (
  <div className="SendReportActionPopoverContent">
    {groups.filter((group) => group.options.length > 0).map((group) => (
      <div className="popover-field" key={group.label}>
        <label>{group.label}</label>
        <div className="field-options">
          {group.options.map((option) => (
            <ActionOption key={option.key} option={option} onSelect={onSelect} />
          ))}
        </div>
      </div>
    ))}
  </div>
);

export function EsvRowActions({
  record,
  loading,
  onMarkRead,
  onMarkDataOps,
}) {
  const [open, setOpen] = useState(false);
  const read = isActionRead(record?.is_read);
  const clearance = normalizeEsvClearance(record?.dataops_status ?? record?.clearance);
  const markOptions = canEditEsvReadStatus() ? [
    {
      key: "read",
      label: "Mark as Read",
      icon: <CarryOutOutlined />,
      disabled: read,
      onClick: () => onMarkRead(record),
    },
    {
      key: "unread",
      label: "Mark as Unread",
      icon: <FieldTimeOutlined />,
      disabled: !read,
      onClick: () => onMarkRead(record),
    },
  ] : [];
  const dataOpsOptions = canEditEsvStatus() ? [
    {
      key: "pending",
      label: "Mark as Pending",
      icon: <FieldTimeOutlined />,
      disabled: clearance === "pending",
      onClick: () => onMarkDataOps(record, "pending"),
    },
    {
      key: "cleared",
      label: "Mark as Cleared",
      icon: <CarryOutOutlined />,
      disabled: clearance === "cleared",
      onClick: () => onMarkDataOps(record, "cleared"),
    },
  ] : [];

  if (loading) {
    return actionSpin;
  }

  if (!markOptions.length && !dataOpsOptions.length) {
    return null;
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomRight"
      overlayClassName="SendReportActionPopover EsvActionMenuPopover"
      arrow={false}
      content={(
        <ActionMenu
          groups={[
            { label: "Mark", options: markOptions },
            { label: "Data Ops Status", options: dataOpsOptions },
          ]}
          onSelect={(option) => {
            setOpen(false);
            option.onClick();
          }}
        />
      )}
    >
      <Button
        type="text"
        className="EsvRowActions__Button"
        icon={<MoreOutlined />}
        aria-label="Actions"
        onClick={(event) => event.stopPropagation()}
      />
    </Popover>
  );
}

export default function EsvBulkActions({
  selectedCount,
  loading,
  canMarkRead,
  canMarkUnread,
  canMarkPending,
  canMarkCleared,
  onMarkRead,
  onMarkDataOps,
}) {
  const [open, setOpen] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const markOptions = canEditEsvReadStatus() ? [
    {
      key: "read",
      label: "Mark as Read",
      icon: <CarryOutOutlined />,
      disabled: !canMarkRead,
      onClick: () => onMarkRead(true),
    },
    {
      key: "unread",
      label: "Mark as Unread",
      icon: <FieldTimeOutlined />,
      disabled: !canMarkUnread,
      onClick: () => onMarkRead(false),
    },
  ] : [];
  const dataOpsOptions = [
    canEditEsvStatus() && canMarkPending ? {
      key: "pending",
      label: "Mark as Pending",
      icon: <FieldTimeOutlined />,
      onClick: () => onMarkDataOps("pending"),
    } : null,
    canEditEsvStatus() && canMarkCleared ? {
      key: "cleared",
      label: "Mark as Cleared",
      icon: <CarryOutOutlined />,
      onClick: () => onMarkDataOps("cleared"),
    } : null,
  ].filter(Boolean);
  const hasActions = markOptions.length > 0 || dataOpsOptions.length > 0;

  const handleConfirm = async () => {
    if (!pendingConfirm || confirmLoading) return;
    setConfirmLoading(true);
    try {
      await pendingConfirm.onClick();
    } finally {
      setConfirmLoading(false);
      setPendingConfirm(null);
    }
  };

  const actionsButton = (
    <Button
      icon={<MoreOutlined />}
      className="actionButton"
      iconPosition="end"
      disabled={selectedCount === 0 || confirmLoading}
    >
      Actions
    </Button>
  );

  const confirmation = (
    <ConfirmationModal
      open={pendingConfirm != null}
      title="Confirm"
      bodyText={pendingConfirm ? confirmationBody(pendingConfirm.label, selectedCount) : ""}
      okText="Confirm"
      cancelText="Cancel"
      loading={confirmLoading}
      onOk={handleConfirm}
      onCancel={() => {
        if (confirmLoading) return;
        setPendingConfirm(null);
      }}
    />
  );

  let bulkControl = null;
  if (hasActions && loading && !confirmLoading) {
    bulkControl = actionSpin;
  } else if (hasActions) {
    bulkControl = (
      <Popover
        open={open}
        onOpenChange={setOpen}
        placement="bottom"
        overlayClassName="SendReportActionPopover EsvActionMenuPopover"
        arrow={false}
        content={(
          <ActionMenu
            groups={[
              { label: "Mark", options: markOptions },
              { label: "Data Ops Status", options: dataOpsOptions },
            ]}
            onSelect={(option) => {
              if (option.disabled) return;
              setOpen(false);
              setPendingConfirm(option);
            }}
          />
        )}
      >
        {actionsButton}
      </Popover>
    );
  }

  if (selectedCount === 0) {
    return (
      <>
        <div className="SendReportActionContainer">
          <span className="counter-text">{`${selectedCount} selected`}</span>
          {hasActions ? actionsButton : null}
        </div>
        {confirmation}
      </>
    );
  }

  return (
    <>
      <div className="SendReportActionContainer">
        <span className="counter-text">{`${selectedCount} selected`}</span>
        {bulkControl}
      </div>
      {confirmation}
    </>
  );
}
